import { ExtBase, ForeignKey, User, Base } from "../../../../core/db/models/index";
import { OrderItemLike, OrderItemAssignmentLike, OrderItem } from "./orderItem";
import { CustomerLike, Customer } from "../customer";
import { Reason, Facility, OrderImage, LegacyModelMixin } from "./index";
import { WithAllType, arrayOrCollection } from "../../../../core/db/index";
import FortumDatabase from "../../index";
import { FortumApp } from "../../../fortumApp";
import { ClientAction } from "../../../synchronizer";

export interface OfflineMixin {
    // IndexedDB / Dexie does not support booleans as indexed type
    /** @see https://github.com/dfahlander/Dexie.js/issues/70#issuecomment-77814592 */
    is_offline: 1 | 0;
}

export interface OrderAssignmentLike extends Base, OfflineMixin
{
    order_id: number;
    user_id: number;

    order?: OrderLike;
    user?: User;
}

export interface OrderLike extends ExtBase, LegacyModelMixin, OfflineMixin
{
    uuid: string;
    work_order_number: string;
    due_date: Date;
    priority: string;
    state: string;
    description: string;
    should_notifiy_user: boolean;
    reason_id: number;
    facility_id: number;
    ordered_by_id: number;

    operator_id?: number;
    operator?: User;
    items?: OrderItemLike[];
    ordered_by?: CustomerLike;
    reason?: Reason;
    facility?: Facility;
    images?: OrderImage[];
    assignments?: OrderAssignmentLike[];
}

export class Order implements Partial<OrderLike>
{
    id: number;
    
    public static readonly STATE_NEW = 'new';
    public static readonly STATE_STARTED = 'started';
    public static readonly STATE_COMPLETED = 'completed';

    public static readonly PRIORITY_NORMAL = 'normal';
    public static readonly PRIORITY_HIGHEST = 'highest';

    public static withAll(db: FortumDatabase, targetOrders: WithAllType<OrderLike>): PromiseLike<OrderLike[]>
    {
        let orders: OrderLike[];

        return arrayOrCollection(targetOrders).then((taskList) =>
        {
            orders = taskList;
            return Promise.all([
                db.DeconReason.where('id').anyOf(orders.map(o => o.reason_id)).toArray(),
                db.DeconFacility.where('id').anyOf(orders.map(o => o.facility_id)).toArray(),
                db.Customer.where('id').anyOf(orders.map(o => o.ordered_by_id).filter(id => id != null)).toArray(),
                db.DeconOrderItem.where('order_id').anyOf(orders.map(o => o.id)).toArray(),
                db.DeconOrderImage.where('order_id').anyOf(orders.map(o => o.id)).toArray(),
                db.User.where('id').anyOf(orders.map(o => o.operator_id).filter(o => o != null)).toArray(),
                db.DeconOrderAssignment.where('order_id').anyOf(orders.map(o => o.id)).toArray()
            ]);
        }).then((results) =>
        {
            let reasons: Reason[] = results[0];
            let facilities: Facility[] = results[1];
            let customers: CustomerLike[] = results[2];
            let orderItems: OrderItemLike[] = results[3];
            let images: OrderImage[] = results[4];
            let operators: User[] = results[5];
            let assignments: OrderAssignmentLike[] = results[6];

            for (let order of orders)
            {
                order.reason = reasons.find(o => o.id == order.reason_id);
                order.facility = facilities.find(o => o.id == order.facility_id);
                order.ordered_by = customers.find(o => o.id == order.ordered_by_id);
                order.items = orderItems.filter(o => o.order_id == order.id);
                order.images = images.filter(o => o.order_id == order.id);
                order.operator = operators.find(o => o.id == order.operator_id);
                order.assignments = assignments.filter(o => o.order_id == order.id);
            }

            return Promise.all([
                Customer.withAll(db, customers),
                OrderAssignment.withAll(db, assignments),
                OrderItem.withAll(db, orderItems)
            ]);
        }).then(() =>
        {
            return orders;
        });
    }
    
    public static complete(app: FortumApp, id: number) {
        return app.db.DeconOrder.update(id, {state: Order.STATE_COMPLETED}).then((result) => {
            return app.synchronizer.createMessageAndPull({
                action: ClientAction.DECONTAMINATION.SET_ORDER_COMPLETED,
                data: {
                    order_id: id,
                }
            }, false)
        });
    }
}

export class OrderAssignment implements Partial<OrderAssignmentLike>
{
    public static withAll(db: FortumDatabase, targets: WithAllType<OrderAssignmentLike>): PromiseLike<OrderAssignmentLike[]>
    {
        let results: OrderAssignmentLike[];

        return arrayOrCollection(targets).then((resolved) =>
        {
            results = resolved;
            return Promise.all([
                db.User.where('id').anyOf(results.map(o => o.user_id).filter(o => o != null)).toArray(),
            ]);
        }).then((data) =>
        {
            let users: User[] = data[0];

            for (let result of results)
            {
                result.user = users.find(o => o.id == result.user_id);
            }
        }).then(() =>
        {
            return results;
        });
    }
}